import { css, cx } from '@linaria/core'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { Button } from '#components/Button'
import { Checkbox } from '#components/Checkbox'
import { Icon } from '#components/Icon'
import { Modal } from '#components/Modal'
import { Select, type SelectEntry } from '#components/Select'
import { InlineErrorDisplay } from '#features/ErrorHandling/ErrorDisplay'
import { createProposedMappings } from '#features/FavoriteSets/createProposedMappings'
import { favoriteSetsVersionNumber, isStoredFavoriteSets, type StoredFavoriteSets } from '#features/FavoriteSets/exportFavoriteSetsAsJson'
import { defaultFavoriteSetId, type FavoriteEntryId, type FavoriteSet, type FavoriteSetId } from '#features/FavoriteSets/FavoriteSet'
import AssignArrow from '#icons/arrow-right-24.svg'
import { style } from '#styles'
import { sharedTestIds } from '#utils/sharedTestIds'

const modalClass = css`
  > ._dialog {
    padding: 16px 32px 16px 32px;
    background: ${style.theme.backgroundSurfacePrimary};
    min-width: 400px;
    max-width: 550px;
    max-height: 80vh;
    
    display: flex;
    flex-direction: column;
    gap: 24px;

    > ._error {
      flex: 1;
      min-height: unset;
      padding-bottom: 16px;
    }
    
    > main {
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex: 1;
      
      > ._mappingGrid {
        display: grid;
        grid-template-columns: max-content 24px 200px max-content;
        gap: 16px 8px;
        width: fit-content;
        padding-left: 32px;
        //align-self: center;
        
        > ._row {
          display: grid;
          grid-column: span 4;
          grid-template-columns: subgrid;
          align-items: center;
          
          > ._name {
            max-width: 200px;
            > label {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: block; 
            }
          }
          
          > ._disabled {
           opacity: 0.5;
          }
          
          > ._stats {
            text-align: right;
            color: ${style.theme.textAccent};
          }
        }
      }
    }
    
    > footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding-top: 8px;
      
      > ._back {
        margin-right: 8px;
      }

      > ._input {
        display: none;
      }
    }
  }
`

export const ImportFavoriteSetModal: FC<{
  open: boolean
  setOpen: (value: boolean) => void
  favoriteSets: FavoriteSet[]
  onAddFavoritesToSet: (favoriteSetId: FavoriteSetId, entryIds: FavoriteEntryId[]) => void
  onAddFavoriteSet: (set: FavoriteSet) => void
}> = ({ open, setOpen, favoriteSets, onAddFavoritesToSet, onAddFavoriteSet }) => {
  const { t } = useTranslation()

  const selectEntries = useMemo(() => {
    const results: SelectEntry<FavoriteSetId | undefined>[] = favoriteSets.map((set, index) => ({
      id: set.id,
      value: set.name || t('shared:favorite-sets.item.placeholder', 'Favorite Set #{{index}}', { index: index + 1 }),
    }))

    results.unshift({
      id: undefined,
      value: t('shared:favorite-sets.import.create-new', 'Create New Set'),
    })
    return results
  }, [favoriteSets, t])

  const [state, setState] = useState<{
    error?: unknown
    importedContent?: StoredFavoriteSets
    mapImportedToExisting: Map<FavoriteSetId, FavoriteSetId>
  }>({
    mapImportedToExisting: new Map(),
  })

  const mappingStats = useMemo(() => {
    const existingSetsById = new Map<FavoriteSetId, FavoriteSet>()
    for (const set of favoriteSets) {
      existingSetsById.set(set.id, set)
    }
    const stats = new Map<FavoriteSetId, string>()
    if (state.importedContent) {
      for (const importedSet of state.importedContent.favoriteSets) {
        const mappedTo = state.mapImportedToExisting.get(importedSet.id)
        const existing = mappedTo ? existingSetsById.get(mappedTo) : undefined
        if (existing) {
          const existingEntryIds = new Set(existing.entries.map((e) => e.id))
          const combinedEntryIds = new Set([...existingEntryIds, ...importedSet.entries.map((e) => e.id)])
          stats.set(importedSet.id, `+${combinedEntryIds.size - existingEntryIds.size}`)
        } else {
          stats.set(importedSet.id, `+${importedSet.entries.length}`)
        }
      }
    }

    return stats
  }, [state.mapImportedToExisting, state.importedContent, favoriteSets])

  const [selectedSets, setSelectedSets] = useState(new Set<FavoriteSetId>())

  const showInitialView = !state.error && !state.importedContent

  useEffect(() => {
    if (state.importedContent) {
      const newSet = new Set<FavoriteSetId>()
      for (const set of state.importedContent.favoriteSets) {
        newSet.add(set.id)
      }
      setSelectedSets(newSet)
    }
  }, [state.importedContent])

  const mergeIntoFavorites = () => {
    if (!state.importedContent) {
      return
    }

    const existingSetIds = new Set<FavoriteSetId>(favoriteSets.map((s) => s.id))

    for (const importedSet of state.importedContent.favoriteSets) {
      const shouldImport = selectedSets.has(importedSet.id)
      if (!shouldImport) {
        continue
      }
      const mappedTo = state.mapImportedToExisting.get(importedSet.id)
      if (mappedTo) {
        onAddFavoritesToSet(
          mappedTo,
          importedSet.entries.map((e) => e.id),
        )
      } else {
        // reuse existing ID if possible, otherwise create a new one
        const newId = existingSetIds.has(importedSet.id) ? (v4() as FavoriteSetId) : importedSet.id
        onAddFavoriteSet({ ...importedSet, id: newId })
      }
    }
  }

  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <Modal lifted shadow open={open} onOpenChange={setOpen} className={modalClass} data-testid={sharedTestIds.favorites.importModal}>
      <h1>{t('favorite-sets.import.title', 'Import Favorites')}</h1>
      {showInitialView && (
        <>
          <main>
            <p>
              {t(
                'shared:favorite-sets.import.import-favorites-text',
                'Favorites that do not exist yet will be added, while your existing favorites will not be changed.',
              )}
            </p>
          </main>
          <footer className="_buttons">
            <Button kind="secondary" onClick={() => setOpen(false)}>
              {t('shared:common.button.cancel')}
            </Button>
            <Button
              kind="accent"
              onClick={() => {
                inputRef.current?.click()
              }}
              data-testid={sharedTestIds.favorites.importButton}
            >
              {t('shared:favorite-sets.import.import-action', 'Import')}
            </Button>
            <input
              className="_input"
              ref={inputRef}
              type="file"
              accept=".maic-fav"
              onChange={async (evt) => {
                const file = evt.target.files?.item(0)
                try {
                  const text = await file?.text()
                  if (!text) {
                    throw new Error('No file content')
                  }

                  let content = JSON.parse(text)
                  if (!isStoredFavoriteSets(content)) {
                    if (Array.isArray(content)) {
                      const allEntriesAreString = content.every((e) => typeof e === 'string')
                      if (allEntriesAreString) {
                        // legacy format: just an array of entry IDs
                        content = {
                          __version: favoriteSetsVersionNumber,
                          __updatedAt: Date.now(),
                          __type: 'maic-frontend-favorite-sets',
                          __maicVersion: `v${VITE_APP_VERSION} (${VITE_COMMIT_SHA})`,
                          __userName: undefined,
                          favoriteSets: [
                            {
                              id: defaultFavoriteSetId,
                              name: 'My Favorites',
                              createdAt: Date.now(),
                              entries: content.map((id) => ({ id, createdAt: Date.now() })),
                            },
                          ],
                        } satisfies StoredFavoriteSets
                      }
                    }
                  }

                  if (!isStoredFavoriteSets(content)) {
                    throw new Error('Invalid file content')
                  }

                  if (content.__version !== favoriteSetsVersionNumber) {
                    throw new Error(`Incompatible version: ${content.__version} expected ${favoriteSetsVersionNumber}`)
                  }
                  setState({ importedContent: content, mapImportedToExisting: createProposedMappings(content, favoriteSets) })
                } catch (e) {
                  setState({ error: e, mapImportedToExisting: new Map() })
                }
              }}
            />
          </footer>
        </>
      )}
      {state.error ? (
        <InlineErrorDisplay
          className="_error"
          error={state.error}
          kind="inline"
          subtitle={t(
            'shared:favorite-sets.import.import-error-text',
            'There was an error importing your favorites. Please make sure you selected a valid export file.',
          )}
          actions={
            <>
              <Button kind="secondary" onClick={() => setOpen(false)}>
                {t('shared:common.button.cancel')}
              </Button>

              <Button
                kind="primary"
                onClick={() =>
                  setState({
                    mapImportedToExisting: new Map(),
                  })
                }
              >
                {t('shared:common.button.back')}
              </Button>
            </>
          }
        />
      ) : undefined}
      {state.importedContent ? (
        <>
          <main>
            <p>
              {t(
                'shared:favorite-sets.import.import-favorites-mapping-text',
                'Please select which sets you want to import and map them to your existing sets.',
              )}
            </p>
            <div className="_mappingGrid">
              {state.importedContent.favoriteSets
                .filter((s) => s.entries.length > 0) // ignore empty sets
                .map((set, index) => {
                  const willBeImported = selectedSets.has(set.id)
                  return (
                    <div className="_row" key={set.id}>
                      <Checkbox
                        className="_name"
                        label={set.name || t('favorite-sets.item.placeholder', 'Favorite Set #{{index}}', { index: index + 1 })}
                        checked={willBeImported}
                        onChange={(e) => {
                          const newSelection = new Set(selectedSets)
                          if (e.currentTarget.checked) {
                            newSelection.add(set.id)
                          } else {
                            newSelection.delete(set.id)
                          }
                          setSelectedSets(newSelection)
                        }}
                      />
                      <Icon className={cx(!willBeImported && '_disabled')} iconUrl={AssignArrow} />
                      <Select
                        options={willBeImported ? selectEntries : []}
                        value={willBeImported ? (state.mapImportedToExisting.get(set.id) ?? undefined) : undefined}
                        onChange={(value) => {
                          const copiedMap = new Map(state.mapImportedToExisting)
                          if (value) {
                            copiedMap.set(set.id, value)
                          } else {
                            copiedMap.delete(set.id)
                          }
                          setState({
                            ...state,
                            mapImportedToExisting: copiedMap,
                          })
                        }}
                        placeholder={
                          willBeImported
                            ? t('shared:favorite-sets.import.select-target', 'Select Target')
                            : t('shared:favorite-sets.import.select-ignored', 'Ignored')
                        }
                        slim
                        disabled={!willBeImported}
                        data-testid={sharedTestIds.favorites.mappingSelect}
                      />
                      <div className="_stats">{willBeImported ? mappingStats.get(set.id) : undefined}</div>
                    </div>
                  )
                })}
            </div>
          </main>
          <footer className="_buttons">
            <Button
              className="_back"
              kind="plain-text"
              onClick={() =>
                setState({
                  mapImportedToExisting: new Map(),
                })
              }
              data-testid={sharedTestIds.favorites.backButton}
            >
              {t('shared:common.button.back')}
            </Button>

            <Button kind="secondary" onClick={() => setOpen(false)} data-testid={sharedTestIds.favorites.cancelButton}>
              {t('shared:common.button.cancel')}
            </Button>

            <Button
              kind="primary"
              onClick={() => {
                try {
                  mergeIntoFavorites()
                  setOpen(false)
                } catch (e) {
                  setState({ ...state, error: e })
                }
              }}
              data-testid={sharedTestIds.favorites.importModalButton}
            >
              {t('shared:favorite-sets.import.import-action', 'Import')}
            </Button>
          </footer>
        </>
      ) : undefined}
    </Modal>
  )
}
